/* eslint-disable max-lines */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LockIcon from '@mui/icons-material/Lock';
import MedicationIcon from '@mui/icons-material/Medication';
import colors from 'common/constants/colors';
import { getLockChipTooltip } from 'common/constants/physicalCountLock';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { find, map } from 'lodash';
import ExportToCsvButton from 'common/components/ExportToCsvButton';
import useBulkQueueEvents from 'common/hooks/useBulkRoomWebSocket';
import usePhysicalCountLockSocket from 'common/hooks/usePhysicalCountLockSocket';
import usePrintLabelContext, {
  MarkAsFilledButton,
  PrintLabelButton,
  PrintLabelProvider,
} from 'common/hooks/usePrintLabelContext';
import useWebSocket from 'common/hooks/useWebSocket';
import WorkQueues from 'enums/workQueue.enum';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentUserLockedItemsById,
  selectLockedItemsUserMapById,
  selectOtherUsersLockedItemsById,
} from 'store/slices/lockedLineItemsSlice';
import { workQueueActions } from 'store/slices/workQueueSlice';
import {
  handleAppLockedLineItems,
  handleLineItemsExpired,
  handleLineItemsLocked,
  handleLineItemsUnlocked,
} from 'store/thunks/lockedLineItemsThunks';
import { filterBulkLabels, listBulkLabelsByPage } from 'store/thunks/workQueueThunks';
import { useWorkQueueTabSwitch } from '../contexts/WorkQueueTabSwitchContext';
import usePhysicalCountLockNotice from '../hooks/usePhysicalCountLockNotice';
import BulkCancelLineItemButton from './BulkCancelLineItemButton';
import BulkSendToCentralFillButton from './BulkSendToCentralFillButton';
import BulkLabelPrintHistoryButton from './BulkLabelPrintHistoryButton';
import BulkLabelsChangeFacilityModeSwitch from './BulkLabelsChangeFacilityModeSwitch';
import BulkLabelsFilterButton from './BulkLabelsFilterButton';
import BulkLabelsFilterStatusBar from './BulkLabelsFilterStatusBar';
import BulkLabelsOuterFilter from './BulkLabelsOuterFilter';
import BulkLabelsTable from './BulkLabelsTable';
import BulkOverride from './BulkOverride';
import BulkTagItemButton from './BulkTagItemButton';
import { useBulkQueueMode } from './ChangeBulkModeContext';
import {
  MultiTransferFacilityButton,
  TransferFacilityProvider,
  useTransferFacilityContext,
} from './MultiTransferFacilityButton';

const URGENT_TAG = 'URGENT';

const BulkLabels = ({ staleLineItems = new Set(), resetStaleLineItems }) => {
  const dispatch = useDispatch();
  const { isTransferFacilityMode, isCentralFillMode } = useBulkQueueMode();
  const { clearLabelItems } = usePrintLabelContext();
  const { clearTransferItems } = useTransferFacilityContext();
  const { sortBy, filter, currentPage } = useSelector(
    ({ workQueue }) => workQueue.bulkLabels,
    shallowEqual
  );
  const lockedInventoryItems = useSelector(({ workQueue }) => workQueue.lockedInventoryItems);
  const fulfillingPharmacies = useSelector(({ auth }) => auth?.fulfillingPharmacies);
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);
  const { isTabSwitch } = useWorkQueueTabSwitch();
  const [showLockedDialog, setShowLockedDialog] = useState(false);

  const lockedItemsWithPharmacyName = useMemo(
    () =>
      map(lockedInventoryItems, (item) => ({
        ...item,
        facilityName:
          find(facilities, { fulfillingPharmacyId: item.fulfillingPharmacyId })?.name ??
          item.fulfillingPharmacyId,
      })),
    [lockedInventoryItems, facilities]
  );

  const lockedItemsGrouped = useMemo(() => {
    const grouped = {};
    lockedItemsWithPharmacyName.forEach((item) => {
      if (!grouped[item.ndc]) {
        grouped[item.ndc] = {
          ndc: item.ndc,
          drugName: item.drugName,
          strength: item.strength,
          // POM-6112 — the read now returns every sibling NDC of a locked product, and siblings
          // usually share a drug name. Carry manufacturer and productId so the dialog can tell
          // those rows apart instead of showing what looks like the same drug twice.
          manufacturer: item.manufacturer,
          productId: item.productId,
          facilities: [],
          // POM-6112 — an NDC can appear once per facility; it's the locked NDC if any of
          // those rows is the literal lock, otherwise every row is a sibling.
          isLocked: false,
        };
      }
      grouped[item.ndc].facilities.push(item.facilityName);
      grouped[item.ndc].isLocked = grouped[item.ndc].isLocked || Boolean(item.isLocked);
    });
    return Object.values(grouped);
  }, [lockedItemsWithPharmacyName]);

  const uniqueFacilityNames = useMemo(
    () => [...new Set(lockedItemsWithPharmacyName.map((i) => i.facilityName))],
    [lockedItemsWithPharmacyName]
  );

  const isMultiFacility = (fulfillingPharmacies?.length ?? 0) > 1;

  const hasLockedItems = lockedItemsGrouped.length > 0;

  // Collapse keeps the banner mounted for the length of its exit transition. Rendering it from
  // the now-empty list would flash "0 medications · 0 facilities" and make MUI measure an
  // already-collapsed height, so the animation never actually plays. Hold the last real content
  // for that stretch instead (POM-6033).
  const lastBannerContentRef = useRef({ items: [], facilities: [] });

  if (hasLockedItems) {
    lastBannerContentRef.current = {
      items: lockedItemsGrouped,
      facilities: uniqueFacilityNames,
    };
  }

  const bannerItems = hasLockedItems ? lockedItemsGrouped : lastBannerContentRef.current.items;
  const bannerFacilities = hasLockedItems
    ? uniqueFacilityNames
    : lastBannerContentRef.current.facilities;

  // Keeps the locked-item list live off the socket, announces every new lock, and evicts
  // anything from the current selection that goes under count while the user holds it (POM-6033).
  usePhysicalCountLockSocket();
  usePhysicalCountLockNotice();

  useEffect(() => {
    if (isTabSwitch(WorkQueues.BULK_LABELS)) {
      dispatch(listBulkLabelsByPage({ page: currentPage }));
    }
  }, [dispatch, currentPage, isTabSwitch]);

  const reloadPage = useCallback(() => {
    clearLabelItems();
    dispatch(
      workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: true })
    );
    dispatch(listBulkLabelsByPage({ page: currentPage })).finally(() => {
      dispatch(
        workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: false })
      );
    });
  }, [dispatch, currentPage, clearLabelItems]);

  const reloadPageWithLoading = useCallback(() => {
    clearLabelItems();
    dispatch(filterBulkLabels({ filter }));
  }, [dispatch, clearLabelItems, filter]);

  const isUrgentFilterActive = filter?.tags?.includes(URGENT_TAG);

  const handleUrgentFilter = useCallback(() => {
    const currentTags = filter?.tags || [];
    const newTags = isUrgentFilterActive
      ? currentTags.filter((t) => t !== URGENT_TAG)
      : [...currentTags, URGENT_TAG];
    dispatch(
      filterBulkLabels({
        filter: {
          ...filter,
          tags: newTags.length > 0 ? newTags : undefined,
          // POM-6330: the urgent print view is always Exact, so an URGENT + OUT_OF_STOCK item
          // stays out of it even if the user had switched the Tags filter to Contains.
          tagMatchMode: undefined,
        },
      })
    );
  }, [dispatch, filter, isUrgentFilterActive]);

  useEffect(() => {
    clearLabelItems();
    clearTransferItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter, sortBy]);

  // The banner can now empty out under the user while the details dialog is open. Drop the open
  // flag with it, or the next lock would reopen a dialog nobody asked for (POM-6033).
  useEffect(() => {
    if (lockedItemsGrouped.length === 0) setShowLockedDialog(false);
  }, [lockedItemsGrouped.length]);

  return (
    <>
      <Collapse in={hasLockedItems} unmountOnExit>
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              mt: 2,
              bgcolor: colors.warningAmberLight,
              border: `1px solid ${colors.warningAmberBorder}`,
              borderRadius: 1,
            }}
          >
            {/* Icon circle */}
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                bgcolor: colors.warningAmber,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FactCheckIcon sx={{ fontSize: '1.4rem', color: colors.white }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Title + subtitle */}
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.3 }}>
                Physical Count in Progress
              </Typography>
              <Typography
                sx={{
                  color: colors.warningAmberText,
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.75,
                  fontSize: '0.75rem',
                }}
              >
                {'• '}
                {bannerItems.length} medication{bannerItems.length !== 1 ? 's' : ''}
                {' · '}
                {bannerFacilities.length}{' '}
                {bannerFacilities.length !== 1 ? 'facilities' : 'facility'}
                {' · locked for fulfillment'}
              </Typography>

              {/* Single chip row: drugs → +N more → facilities */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {bannerItems.slice(0, 3).map((item) => (
                  <Tooltip key={item.ndc} title={getLockChipTooltip(item.isLocked)}>
                    <Chip
                      icon={
                        item.isLocked ? (
                          <LockIcon sx={{ fontSize: '0.75rem !important' }} />
                        ) : (
                          <MedicationIcon sx={{ fontSize: '0.75rem !important' }} />
                        )
                      }
                      label={`${item.drugName || item.ndc}${item.strength ? ` ${item.strength}` : ''}`}
                      size='small'
                      sx={{
                        height: 22,
                        bgcolor: item.isLocked ? colors.white : 'transparent',
                        border: `1px solid ${colors.warningAmberBorder}`,
                        borderStyle: item.isLocked ? 'solid' : 'dashed',
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 500 },
                      }}
                    />
                  </Tooltip>
                ))}
                {bannerItems.length > 3 && (
                  <Chip
                    label={`+${bannerItems.length - 3} more`}
                    size='small'
                    sx={{
                      height: 22,
                      bgcolor: colors.warningAmber,
                      border: `1px solid ${colors.warningAmber}`,
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: colors.white,
                      },
                    }}
                  />
                )}
                {/* Divider between drug chips and facility chips */}
                <Box
                  sx={{
                    width: '2px',
                    height: 20,
                    bgcolor: colors.warningAmber,
                    alignSelf: 'center',
                    mx: 0.5,
                    flexShrink: 0,
                    borderRadius: '1px',
                  }}
                />
                {bannerFacilities.map((name) => (
                  <Chip
                    key={name}
                    icon={<BusinessIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label={name}
                    size='small'
                    sx={{
                      height: 22,
                      bgcolor: colors.warningAmberBorder,
                      border: `1px solid ${colors.warningAmber}`,
                      '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 500 },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* View Details button */}
            <Button
              size='small'
              variant='outlined'
              endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={() => setShowLockedDialog(true)}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                px: 1.5,
                py: 0.5,
                borderColor: colors.warningAmberBorder,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: colors.warningAmber,
                  bgcolor: colors.warningAmberLight,
                },
              }}
            >
              View Details
            </Button>
          </Box>

          <Dialog
            open={showLockedDialog}
            onClose={() => setShowLockedDialog(false)}
            maxWidth='md'
            fullWidth
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: colors.warningAmber,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FactCheckIcon sx={{ fontSize: '1.4rem', color: colors.white }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Physical Count in Progress
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {bannerItems.length} medication{bannerItems.length !== 1 ? 's' : ''} locked
                  {bannerFacilities.length > 0 && ` · ${bannerFacilities.join(', ')}`}
                </Typography>
              </Box>
              <IconButton size='small' onClick={() => setShowLockedDialog(false)} sx={{ ml: 1 }}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <Divider />
            <DialogContent sx={{ px: 3, py: 2 }}>
              <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                Fulfillment for these medications is blocked until the physical count is complete at
                each facility. Contact the inventory manager to check count status.
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Drug</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Strength</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Manufacturer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>NDC</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product ID</TableCell>
                    {isMultiFacility && (
                      <TableCell sx={{ fontWeight: 700 }}>Locked At (Facilities)</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bannerItems.map((item) => (
                    <TableRow key={item.ndc}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: colors.drugIconBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <MedicationIcon
                              sx={{ fontSize: '0.9rem', color: colors.drugIconColor }}
                            />
                          </Box>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {item.drugName || '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{item.strength || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{item.manufacturer || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                          {item.ndc}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                          {item.productId || '—'}
                        </Typography>
                      </TableCell>
                      {isMultiFacility && (
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.facilities.map((facility) => (
                              <Chip
                                key={facility}
                                icon={<BusinessIcon sx={{ fontSize: '0.75rem !important' }} />}
                                label={facility}
                                size='small'
                                sx={{
                                  height: 22,
                                  bgcolor: colors.warningAmberLight,
                                  border: `1px solid ${colors.warningAmberBorder}`,
                                  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant='outlined' onClick={() => setShowLockedDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      </Collapse>
      <Grid
        container
        sx={{ minHeight: 40, mt: 2 }}
        alignItems='center'
        justifyContent='space-between'
      >
        <Grid item>
          <BulkLabelsChangeFacilityModeSwitch />
        </Grid>
        <Grid item>
          <Grid container gap={1}>
            {isTransferFacilityMode && <MultiTransferFacilityButton onSuccess={reloadPage} />}
            {isCentralFillMode && <BulkSendToCentralFillButton />}
            {!isTransferFacilityMode && !isCentralFillMode && (
              <>
                <Button
                  variant={isUrgentFilterActive ? 'contained' : 'outlined'}
                  onClick={handleUrgentFilter}
                  color='info'
                  sx={{ height: 33 }}
                >
                  Urgent
                </Button>
                <BulkCancelLineItemButton onSuccess={reloadPageWithLoading} />
                <BulkTagItemButton onSuccess={reloadPageWithLoading} />
                <PrintLabelButton onSuccess={reloadPage} onWorkQueue isBulk />
                <MarkAsFilledButton onSuccess={reloadPage} />
                <BulkOverride />
              </>
            )}
            <BulkLabelPrintHistoryButton />
            <ExportToCsvButton />
          </Grid>
        </Grid>
      </Grid>
      <Grid container sx={{ py: 2 }} alignItems='center' spacing={1}>
        <Grid item sx={{ minHeight: 40, flexGrow: 2 }}>
          <BulkLabelsFilterStatusBar resetStaleLineItems={resetStaleLineItems} />
        </Grid>
        <Grid item>
          <BulkLabelsOuterFilter />
        </Grid>
        <Grid item>
          <BulkLabelsFilterButton />
        </Grid>
        <BulkLabelsTable
          staleLineItems={staleLineItems}
          lockedInventoryItems={lockedInventoryItems}
        />
      </Grid>
    </>
  );
};

BulkLabels.propTypes = {
  staleLineItems: PropTypes.instanceOf(Set),
  resetStaleLineItems: PropTypes.func.isRequired,
};

BulkLabels.defaultProps = {
  staleLineItems: new Set(),
};

const BulkLabelsWithProvider = () => {
  const dispatch = useDispatch();
  const preset = useSelector(({ workQueue }) => workQueue.preset);
  const webSocketMessages = useSelector(({ webSocket }) => webSocket?.messages);
  const currentUserId = useSelector(({ auth }) => auth?.userId);
  const [staleLineItems, setStaleLineItems] = useState(new Set());
  const lastProcessedIndexRef = useRef(0);

  const currentUserLockedItems = useSelector((state) =>
    selectCurrentUserLockedItemsById(state, currentUserId)
  );
  const otherUsersLockedItems = useSelector((state) =>
    selectOtherUsersLockedItemsById(state, currentUserId)
  );
  const lockedItemsUserMap = useSelector((state) =>
    selectLockedItemsUserMapById(state, currentUserId)
  );

  const mainWebSocket = useWebSocket();

  const resetStaleLineItems = useCallback(() => {
    setStaleLineItems(new Set());
    const currentMessageCount = webSocketMessages?.length || 0;
    lastProcessedIndexRef.current = currentMessageCount;
  }, [webSocketMessages]);

  const handleRefresh = useCallback(() => {
    resetStaleLineItems();
    dispatch(
      workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: true })
    );
    dispatch(listBulkLabelsByPage({ page: 1 })).finally(() => {
      dispatch(
        workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: false })
      );
    });
  }, [resetStaleLineItems, dispatch]);

  const bulkRoomWebSocket = useBulkQueueEvents(mainWebSocket.socket, handleRefresh);

  const websocketMessages = useMemo(() => webSocketMessages || [], [webSocketMessages]);

  useEffect(() => {
    const newMessages = websocketMessages.slice(lastProcessedIndexRef.current);

    if (newMessages.length === 0) {
      return;
    }

    const lineItemsMessages = newMessages.filter(
      (msg) =>
        msg.event === 'line_items_locked' ||
        msg.event === 'line_items_unlocked' ||
        msg.event === 'line_items_expired' ||
        msg.event === 'app_locked_line_items' ||
        msg.event === 'bulk_items_filled' ||
        msg.event === 'bulk_items_printed'
    );

    const handleBulkItemsChange = (msg) => {
      if (msg.data && Array.isArray(msg.data)) {
        setStaleLineItems((prev) => {
          const newStaleItems = new Set(prev);

          msg.data.forEach((item) => {
            const actionUserId = item.actionUser?.pomUserId;
            const isDifferentUser = actionUserId && actionUserId !== currentUserId;

            if (isDifferentUser && item.lineItemIds && Array.isArray(item.lineItemIds)) {
              item.lineItemIds.forEach((id) => newStaleItems.add(id));
            }
          });

          return newStaleItems;
        });
      }
    };

    const eventHandlers = {
      line_items_locked: (msg) => dispatch(handleLineItemsLocked({ data: msg.data })),
      line_items_unlocked: (msg) => dispatch(handleLineItemsUnlocked({ data: msg.data })),
      line_items_expired: (msg) => dispatch(handleLineItemsExpired({ data: msg.data })),
      app_locked_line_items: (msg) => dispatch(handleAppLockedLineItems({ data: msg.data })),
      bulk_items_filled: handleBulkItemsChange,
      bulk_items_printed: handleBulkItemsChange,
    };

    lineItemsMessages.forEach((msg) => {
      const handler = eventHandlers[msg.event];
      if (handler) {
        handler(msg);
      }
    });

    lastProcessedIndexRef.current = websocketMessages.length;
  }, [websocketMessages, dispatch, currentUserId]);

  useEffect(() => {
    const shouldJoinBulkRoom =
      preset === WorkQueues.BULK_LABELS &&
      mainWebSocket.isConnected &&
      !bulkRoomWebSocket.isBulkRoomJoined;

    if (shouldJoinBulkRoom) {
      bulkRoomWebSocket.joinBulkRoom();
    }
  }, [
    preset,
    mainWebSocket.isConnected,
    bulkRoomWebSocket.isBulkRoomJoined,
    bulkRoomWebSocket.joinBulkRoom,
    bulkRoomWebSocket,
  ]);

  useEffect(() => {
    if (preset !== WorkQueues.BULK_LABELS && bulkRoomWebSocket.isBulkRoomJoined) {
      bulkRoomWebSocket.leaveBulkRoom();
    }
  }, [
    preset,
    bulkRoomWebSocket.isBulkRoomJoined,
    bulkRoomWebSocket.leaveBulkRoom,
    bulkRoomWebSocket,
  ]);

  if (preset !== WorkQueues.BULK_LABELS) return null;

  return (
    <PrintLabelProvider
      initialValue={[...currentUserLockedItems]}
      lockedByOtherUsers={otherUsersLockedItems}
      lockedItemsUserMap={lockedItemsUserMap}
    >
      <TransferFacilityProvider>
        <BulkLabels staleLineItems={staleLineItems} resetStaleLineItems={resetStaleLineItems} />
      </TransferFacilityProvider>
    </PrintLabelProvider>
  );
};

export default BulkLabelsWithProvider;
